import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { setAdminUserGroups } from '../api';
import SearchField, { isSearchReady } from './SearchField';
import PagedPicker from './PagedPicker';
import Button from './ui/Button';

/**
 * Which groups a person belongs to.
 *
 * That is the whole of their access now: a group carries its department and its
 * rights, so there is nothing else to choose here.
 *
 * What shows is what the person is already in — that is the answer to "what
 * does this account have", which is why the block is opened most of the time.
 * Adding them somewhere is a second question, and the search answers it: as
 * soon as it matches, those groups appear among the ticks and can be ticked.
 * Listing every group by default would bury the two or three that matter.
 */
function AdminUserGroups({
  user,
  groups,
  assignableGroups = groups,
  readOnly = false,
  onChanged,
}) {
  const { t } = useTranslation();

  const initial = useMemo(
    () => new Set(user.groupIds ?? []),
    [user],
  );

  // Granted by the identity provider and rewritten at each sign-in; the
  // server keeps them whatever is sent, so they are shown but locked.
  const fromProvider = useMemo(
    () =>
      new Set(
        (user.groups ?? [])
          .filter((group) => group.source === 'idp')
          .map((group) => group.groupId),
      ),
    [user],
  );

  const [selected, setSelected] = useState(initial);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [query, setQuery] = useState('');
  const [isBrowsing, setBrowsing] = useState(false);

  // The row is re-rendered with fresh data after every save.
  useEffect(() => {
    setSelected(initial);
  }, [initial]);

  const isDirty =
    selected.size !== initial.size ||
    [...selected].some((groupId) => !initial.has(groupId));

  function toggle(groupId) {
    setError('');
    setSuccessMessage('');

    setSelected((current) => {
      const next = new Set(current);

      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }

      return next;
    });
  }

  async function handleSave() {
    try {
      setIsSaving(true);
      setError('');
      setSuccessMessage('');

      await setAdminUserGroups(user.userId, [...selected]);

      setSuccessMessage(t('adminPanel.saveGroupsSuccess'));

      if (onChanged) {
        await onChanged();
      }
    } catch (submitError) {
      console.error('Failed to save groups:', submitError);
      setError(submitError?.message || t('adminPanel.errorGeneric'));
    } finally {
      setIsSaving(false);
    }
  }

  // What the person holds, plus whatever the search turned up. A group unticked
  // a moment ago stays on screen: it was in `initial`, and disappearing under
  // the cursor is not what unticking should do.
  //
  // Only groups the caller may hand out are found by the search; one held
  // outside their departments stays visible but cannot be unticked.
  const assignableIds = new Set(
    assignableGroups.map((group) => group.group_id),
  );

  const visibleGroups = groups.filter((group) => {
    if (initial.has(group.group_id) || selected.has(group.group_id)) {
      return true;
    }

    if (readOnly || !assignableIds.has(group.group_id) || !isSearchReady(query)) {
      return false;
    }

    const needle = query.trim().toLowerCase();

    return [group.group_name, group.department_name]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(needle);
  });

  // Platform groups first, then each department: the same order the group
  // matrix uses, so the two blocks read alike.
  const byDepartment = visibleGroups.reduce((map, group) => {
    const key = group.is_platform
      ? t('adminPanel.platformScope')
      : group.department_name;

    return map.set(key, [...(map.get(key) ?? []), group]);
  }, new Map());

  return (
    <div className="admin-user-groups">
      {error && (
        <div className="login-message login-message-error" role="alert">
          <i className="fa-solid fa-circle-exclamation" />
          <span>{error}</span>
        </div>
      )}

      {successMessage && (
        <div className="login-message login-message-success" role="status">
          <i className="fa-solid fa-circle-check" />
          <span>{successMessage}</span>
        </div>
      )}

      {!readOnly && (
        <div className="admin-user-search-row">
          <SearchField
            value={query}
            onChange={setQuery}
            placeholder={t('adminPanel.groupSearchPlaceholder')}
            ariaLabel={t('adminPanel.groupSearchAriaLabel')}
          />

          <Button
            variant="secondary"
            aria-pressed={isBrowsing}
            onClick={() => setBrowsing((browsing) => !browsing)}
          >
            <i className="fa-solid fa-list" />
            {isBrowsing
              ? t('adminPanel.hideAllUsers')
              : t('adminPanel.showAllUsers')}
          </Button>
        </div>
      )}

      {!readOnly && isBrowsing && (
        <PagedPicker
          items={assignableGroups}
          getKey={(group) => group.group_id}
          selectedKeys={[...selected]}
          onToggle={toggle}
          renderPrimary={(group) => group.group_name}
          renderSecondary={(group) =>
            group.is_platform
              ? t('adminPanel.platformScope')
              : group.department_name
          }
        />
      )}

      {byDepartment.size === 0 && (
        <p className="admin-user-list-empty">
          {isSearchReady(query)
            ? t('adminPanel.noGroupsFound')
            : t('adminPanel.noGroupsYet')}
        </p>
      )}

      {[...byDepartment.entries()].map(([department, departmentGroups]) => (
        <div className="admin-user-group-scope" key={department}>
          <h5>{department}</h5>

          <div className="admin-group-options">
            {departmentGroups.map((group) => (
              <label
                key={group.group_id}
                className="admin-group-option"
                title={
                  fromProvider.has(group.group_id)
                    ? t('adminPanel.groupFromProvider')
                    : (group.rights ?? []).join(', ')
                }
              >
                <input
                  type="checkbox"
                  checked={selected.has(group.group_id)}
                  disabled={
                    isSaving ||
                    readOnly ||
                    fromProvider.has(group.group_id) ||
                    !assignableIds.has(group.group_id)
                  }
                  onChange={() => toggle(group.group_id)}
                />

                <span>{group.group_name}</span>

                {fromProvider.has(group.group_id) && (
                  <span className="admin-sso-badge">SSO</span>
                )}
              </label>
            ))}
          </div>
        </div>
      ))}

      {!readOnly && (
        <div className="account-actions">
          <Button
            variant="primary"
            disabled={isSaving || !isDirty}
            onClick={handleSave}
          >
            {isSaving
              ? t('adminPanel.saving')
              : t('adminPanel.saveGroupsButton')}
          </Button>
        </div>
      )}
    </div>
  );
}

export default AdminUserGroups;
